import { useMemo, useState } from 'react';
import Button from '../../../shared/components/Button';
import { useStaff } from '../../staff/hooks/useStaff';
import {
  useCreateStaffAssignment,
  useDepartmentAssignments,
  useRemoveStaffAssignment,
} from '../hooks/useDepartments';
import type { Department, DepartmentRole } from '../types';

const selectClass =
  'px-3 border border-[#D9D9D9] bg-muted text-foreground rounded-[10px] h-[30px] text-[14px] focus:outline-none';

interface StaffAssignmentsPanelProps {
  department: Department;
  roles: DepartmentRole[];
  canManage: boolean;
}

/**
 * PE-2147 — who holds which role in the selected department. Staff picker is
 * `GET /iam/users/staff` (COLLECTIVE-scope only, which is exactly what the
 * assignment endpoint accepts). A person may hold several roles across (or
 * within) departments; the backend only rejects the same (user, role) twice.
 */
export const StaffAssignmentsPanel = ({ department, roles, canManage }: StaffAssignmentsPanelProps) => {
  const { data: assignments = [], isLoading, error } = useDepartmentAssignments(department.id);
  const { data: staff = [] } = useStaff();
  const create = useCreateStaffAssignment();
  const remove = useRemoveStaffAssignment();

  const [userId, setUserId] = useState('');
  const [roleId, setRoleId] = useState('');

  const staffById = useMemo(() => new Map(staff.map((s) => [s.id, s])), [staff]);
  const rolesById = useMemo(() => new Map(roles.map((r) => [r.id, r])), [roles]);
  const activeRoles = roles.filter((r) => r.isActive);

  const handleAdd = async () => {
    if (!userId || !roleId) return;
    await create.mutateAsync({ userId, departmentId: department.id, departmentRoleId: roleId });
    setUserId('');
    setRoleId('');
  };

  return (
    <div className="space-y-3">
      <h3 className="text-base font-semibold text-foreground">Staff</h3>

      {canManage && (
        <div className="flex flex-wrap gap-2 items-center">
          <select className={selectClass} value={userId} onChange={(e) => setUserId(e.target.value)}>
            <option value="">Select staff member</option>
            {staff.map((s) => (
              <option key={s.id} value={s.id}>
                {s.fullName} · {s.email}
              </option>
            ))}
          </select>
          <select className={selectClass} value={roleId} onChange={(e) => setRoleId(e.target.value)}>
            <option value="">Select role</option>
            {activeRoles.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
          <Button size="sm" onClick={handleAdd} disabled={!userId || !roleId || create.isPending} loading={create.isPending}>
            Assign
          </Button>
          {activeRoles.length === 0 && (
            <span className="text-xs text-muted-foreground">Create an active role first.</span>
          )}
        </div>
      )}

      {error && <p className="text-sm text-destructive">Could not load assignments.</p>}
      {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {!isLoading && !assignments.length && (
        <p className="text-sm text-muted-foreground">Nobody is assigned to this department yet.</p>
      )}

      {assignments.length > 0 && (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-muted-foreground border-b border-border">
              <th className="py-2 pr-3 font-medium">Staff member</th>
              <th className="py-2 pr-3 font-medium">Role</th>
              <th className="py-2 pr-3 font-medium">Since</th>
              {canManage && <th className="py-2 font-medium" />}
            </tr>
          </thead>
          <tbody>
            {assignments.map((a) => {
              const person = staffById.get(a.userId);
              const role = a.departmentRole ?? rolesById.get(a.departmentRoleId);
              return (
                <tr key={a.id} className="border-b border-border last:border-0">
                  <td className="py-2 pr-3 text-foreground">
                    {person ? (
                      <>
                        {person.fullName}
                        <span className="block text-xs text-muted-foreground">{person.email}</span>
                      </>
                    ) : (
                      <span className="font-mono text-xs">{a.userId}</span>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-foreground">
                    {role?.name ?? <span className="font-mono text-xs">{a.departmentRoleId}</span>}
                    {role && !role.isActive && <span className="ml-1 text-xs text-muted-foreground">(inactive)</span>}
                  </td>
                  <td className="py-2 pr-3 text-muted-foreground">{new Date(a.createdAt).toLocaleDateString()}</td>
                  {canManage && (
                    <td className="py-2 text-right">
                      <button
                        onClick={() => remove.mutate({ id: a.id, departmentId: department.id })}
                        disabled={remove.isPending}
                        className="text-sm text-red-600 hover:underline disabled:opacity-50">
                        Remove
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};
